import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit, AlertCircle, FileCheck, Link as LinkIcon } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LabsAppBar from "../../components/LabsAppBar";
import { ActionButton } from '../../components/LabsAppBar';
import TableComponent from '@components/shared/TableComponent';
import { ColumnDef } from '@tanstack/react-table';
import { Contract } from '../../types/contract';
import { fetchAllContracts, deleteContract } from '../../services/contractApiService'; 
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotifications();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState<string | null>(null); // State to track which contract's link was copied

  let breadcrumbs = [];
  const newContractButton: ActionButton = {
    label: 'New Contract',
    path: '/labs/contracts/new/edit/details',
    iconName: 'PlusCircle',
    variant: 'primary',
    // state will be passed via Link component's state prop implicitly
  };

  let actions: ActionButton[] = [newContractButton];
  let pageTitle = '';
  let pageIcon: string | undefined;

  switch (location.pathname) {
    case '/labs':
    case '/labs/contracts':
      pageTitle = 'Data Contracts';
      pageIcon = 'SquareCheckBig';
      // actions are already defined above
      break;
    default:
      pageTitle = 'Data Contracts';
      pageIcon = 'SquareCheckBig';
      // actions are already defined above
      break;
  }

  breadcrumbs = [{ name: pageTitle, iconName: pageIcon }];

  const loadContracts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchAllContracts(); // Assuming fetchAllContracts exists
      console.log('ContractsPage: Data received from fetchAllContracts:', data);
      setContracts(data);
    } catch (err) {
      console.error('Failed to fetch contracts:', err);
      setError('Failed to load contracts. Please try again.');
      showNotification('error', 'Failed to load contracts.');
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadContracts();
    // Check for success message from ContractReviewPage
    if (localStorage.getItem('contractAccepted')) {
      showNotification('success', 'Contract accepted successfully');
      localStorage.removeItem('contractAccepted');
    }
  }, [loadContracts, showNotification]);

  const handleRowClick = (contract: Contract) => {
    if (contract.status === 'accepted') {
      navigate(`/labs/contracts/${contract.id}/accepted-details`, { state: { contractId: contract.id, contractName: contract.name } });
    } else {
      navigate(`/labs/contracts/${contract.id}/edit/details`, { state: { contractName: contract.name } }); // Navigate to the details tab
    }
  };

  const handleDeleteClick = (contract: Contract) => {
    setContractToDelete(contract);
    setShowConfirmDialog(true);
  };

  const confirmDelete = useCallback(async () => {
    if (contractToDelete) {
      try {
        await deleteContract(contractToDelete.id);
        showNotification('success', `Contract "${contractToDelete.name}" deleted successfully`);
        loadContracts(); // Reload contracts after deletion
      } catch (err) {
        console.error('Failed to delete contract:', err);
        showNotification('error', `Failed to delete contract "${contractToDelete.name}".`);
      } finally {
        setShowConfirmDialog(false);
        setContractToDelete(null);
      }
    }
  }, [contractToDelete, loadContracts, showNotification]);

  const cancelDelete = useCallback(() => {
    setShowConfirmDialog(false);
    setContractToDelete(null);
  }, []);

  const handleGetShareLink = useCallback((contract: Contract) => {
    if (contract.share_token) {
      const shareLink = `${window.location.origin}/contracts/share/${contract.share_token}`;
      navigator.clipboard.writeText(shareLink)
        .then(() => {
          setShowCopiedMessage(contract.id); // Set the ID of the copied contract
          setTimeout(() => setShowCopiedMessage(null), 2000); // Hide after 2 seconds
          showNotification('success', 'Share link copied to clipboard.');
        })
        .catch(err => {
          console.error('Failed to copy share link:', err);
          showNotification('error', 'Failed to copy share link.');
        });
    } else {
      showNotification('info', 'No share link available for this contract.');
    }
  }, [showNotification]);

  const contractColumns: ColumnDef<Contract>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const toPath = row.original.status === 'accepted'
          ? `/labs/contracts/${row.original.id}/accepted-details`
          : `/labs/contracts/${row.original.id}/edit/details`;
        return (
          <Link
            to={toPath}
            state={{ contractId: row.original.id, contractName: row.original.name }}
            className="flex items-center group" // Add group for hover effects
            onClick={(e) => e.stopPropagation()} // Prevent TableComponent's onRowClick
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full mr-1">
              <FileCheck size={20} className="text-blue-500" />
            </div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors duration-200">
              {row.original.name}
            </span>
          </Link>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => row.original.description || 'N/A',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${
          row.original.status === 'accepted' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
          row.original.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {row.original.status}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: info => new Date(info.getValue() as string).toLocaleDateString(),
    },
    {
      accessorKey: 'updated_at',
      header: 'Last Modified',
      cell: info => new Date(info.getValue() as string).toLocaleDateString(),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          {row.original.share_token && ( // Conditionally render share link button
            <button
              className="text-gray-400 hover:text-purple-600 dark:text-gray-500 dark:hover:text-purple-300 transition-colors duration-200 relative" // Add relative for positioning
              onClick={(e) => {
                e.stopPropagation();
                handleGetShareLink(row.original);
              }}
            >
              <LinkIcon className="h-5 w-5" />
              {showCopiedMessage === row.original.id && ( // Conditionally render message
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
                >
                  Copied!
                </motion.div>
              )}
            </button>
          )}
          <button
            className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.original); // Use handleRowClick for editing
            }}
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row.original);
            }}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ], [handleRowClick, handleDeleteClick, handleGetShareLink]); // Add handleGetShareLink to dependencies

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-10 max-w-9xl mx-auto">
          <div className="space-y-8">
            <TableComponent
              title="Data Contracts"
              data={contracts}
              columns={contractColumns}
              onRowClick={handleRowClick}
              isLoading={isLoading}
              isError={!!error}
              errorContent={
                <div className="flex items-center justify-center py-4 text-red-500">
                  <AlertCircle className="h-6 w-6 mr-3" />
                  <span>{error}</span>
                </div>
              }
              buttons={<></>} // Moved "Add Contract" to LabsAppBar
            />
          </div>
        </div>
      </div>
      {showConfirmDialog && contractToDelete && (
        <ConfirmDialog
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Confirm Contract Deletion"
          message={`Are you sure you want to delete the contract "${contractToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isDanger={true}
        />
      )}
    </div>
  );
};

export default ContractsPage;
